import NextPayableModel from './NextPayable';
import RentAmountChangeModel from './RentAmountChange';
import { PROPERTY_STATUS } from '../constants/OnboardingConstants';
import { isObjectEmpty } from '@common/paymentsUtils/inputValidator';
import { IPropertyInfoNumericInputModel, IPropertyModel } from './Types';
import { formatTimestampToYMDString } from '@common/paymentsUtils/dateUtils';
import RentalProtectionPolicyModel from '@rental-manager/models/RentalProtectionPolicy';

export const getFirstName = (fullName: string) => fullName && fullName.split(' ')[0];
export const getLastName = (fullName: string) => fullName && fullName.split(' ')[1];

const emptyTerm = () => ({
  termNotStarted: true,
});

function fromPropertyInfo(propertyInfo: IPropertyInfoNumericInputModel, today: Date) {
  if (isObjectEmpty(propertyInfo)) {
    return emptyTerm();
  }

  const {
    status,
    rentTerms = {
      amount: 0,
      firstDueDate: 0,
      monthToMonth: false,
      numberOfPayments: 12,
      rentAmountChangeRequest: {},
      rentTermsKey: '',
      paymentDateForFirstMonth: 0,
      dueLastDayOfMonth: false,
      monthlyPaymentDate: 0,
    },
    rentalProtectionPolicy,
    endDateForPayments,
    nextPayable = {
      dueDate: 0,
      scheduleMonth: '',
      amount: 0,
      fee: 0,
      totalAmount: 0,
      isScheduled: false,
    },
    tenantName,
    tenantEmail,
    tenantOnBoarded,
    oneTimePaymentLimits = {},
    nextPaymentPlanInstallmentNumber,
  } = propertyInfo;

  const termEnded = status === PROPERTY_STATUS.TERM_COMPLETE || status === PROPERTY_STATUS.TERM_EXPIRED;

  const {
    amount,
    firstDueDate,
    monthToMonth,
    numberOfPayments,
    rentAmountChangeRequest = {
      effectiveDueDate: '',
      newAmount: 0,
      renterAcceptanceDate: '',
    },
    rentTermsKey,
    paymentDateForFirstMonth = 0,
    dueLastDayOfMonth = false,
    monthlyPaymentDate = 0,
  } = rentTerms;

  const canCreateOneTimePaymentRequest = Object.keys(oneTimePaymentLimits).length > 0;

  return {
    canCreateOneTimePaymentRequest,
    tenantName: tenantName && String(tenantName),
    tenantFirstName: tenantName && getFirstName(tenantName),
    tenantLastName: tenantName && getLastName(tenantName),
    tenantEmail: tenantEmail && String(tenantEmail),
    tenantOnBoarded: Boolean(tenantOnBoarded),
    amount: amount && String(amount),
    firstDueDate: formatTimestampToYMDString(firstDueDate),
    paymentDateForFirstMonth: formatTimestampToYMDString(paymentDateForFirstMonth),
    monthToMonth: Boolean(monthToMonth),
    numberOfPayments: numberOfPayments && Number(numberOfPayments),
    propertyStatus: status && String(status),
    rentTermsKey: rentTermsKey && String(rentTermsKey),
    endDateForPayments: formatTimestampToYMDString(endDateForPayments),
    rentAmountChangeRequest: RentAmountChangeModel(
      rentAmountChangeRequest,
      formatTimestampToYMDString(firstDueDate),
      formatTimestampToYMDString(endDateForPayments),
      NextPayableModel(nextPayable),
      today,
      monthlyPaymentDate,
    ),
    termEnded,
    nextPayable: NextPayableModel(nextPayable),
    oneTimePaymentLimits,
    rentalProtectionPolicy: rentalProtectionPolicy && RentalProtectionPolicyModel(rentalProtectionPolicy),
    nextPaymentPlanInstallmentNumber,
    dueLastDayOfMonth,
    monthlyPaymentDate,
  };
}

export default (propertyInfoV2: IPropertyModel, today = new Date()) => {
  let currentTerm = propertyInfoV2?.current;
  currentTerm = fromPropertyInfo(currentTerm, today);

  let nextTerm = propertyInfoV2?.next;

  nextTerm = fromPropertyInfo(nextTerm, today);

  if (currentTerm.termNotStarted && nextTerm.termNotStarted) {
    return { currentTerm, nextTerm };
  }

  return { currentTerm, nextTerm };
};
